import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

// ---------------------------------------------------------------------------
// SESSION HYGIENE
// ---------------------------------------------------------------------------
// Browser-side nuke of the access_token cookie. The server-side /auth/logout
// also calls `response.delete_cookie("access_token")`, but if that request
// fails (offline, 5xx, aborted) we still need to be sure the stale cookie
// doesn't survive to be picked up by a cookie-fallback on the next session.
// Multiple variants cover different ingress / path / SameSite combinations.
function clearSessionCookies() {
  if (typeof document === "undefined") return;
  const variants = [
    "access_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/",
    "access_token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax",
    "access_token=; max-age=0; path=/",
  ];
  for (const v of variants) {
    try { document.cookie = v; } catch { /* noop */ }
  }
}

// Decode a JWT payload (middle segment) safely. Returns null if the token is
// malformed. NEVER touches / returns the signature segment.
function decodeTokenPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    // base64-url → base64 → UTF-8 JSON
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
    const json = atob(b64 + pad);
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch { return null; }
}

// Full local-session wipe. Does NOT hit the server. Used on mismatch
// detection, logout fallback, or any other situation where we need to be
// certain nothing lingers.
function wipeLocalSession() {
  try { localStorage.removeItem("token"); } catch { /* noop */ }
  clearSessionCookies();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [loading, setLoading] = useState(true);

  // ---------------------------------------------------------------------
  // CROSS-TAB SESSION INVALIDATION (V10 hardening)
  //
  // `localStorage` is a single key-space per origin. If a second tab/window
  // on the same browser signs in as a DIFFERENT account, it will overwrite
  // this tab's `token` under us. Our mount reconciliation (V9) only runs
  // on page load, so the already-mounted stale tab would keep its React
  // state (showing Owner) while the global axios interceptor silently
  // starts sending the new tab's token on every request — authenticating
  // as the other user. The user approved this exact tab stays visually
  // consistent OR goes to /login; it NEVER silently morphs mid-session.
  //
  // So: when another tab writes to the `token` key:
  //   • Nothing meaningful changed (same sub as our current user) → no-op.
  //   • Token removed, or decode fails, or sub differs from current user
  //     → invalidate THIS tab's session and redirect to /login.
  //
  // Pure event-driven — no polling, no recurring /auth/me calls.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onStorage(e) {
      // `storage` only fires in OTHER tabs for the same origin — never
      // in the tab that initiated the change. Guard the key explicitly.
      if (e.key !== "token" && e.key !== null) return;

      const newToken = (() => {
        try { return localStorage.getItem("token"); } catch { return null; }
      })();

      // Case 1: token wiped elsewhere (logout in another tab) → sign out here too.
      if (!newToken) {
        if (user) {
          wipeLocalSession();
          setUser(false);
          if (!window.location.pathname.startsWith("/login")) {
            window.location.replace("/login");
          }
        }
        return;
      }

      // Case 2: another user signed in on this origin → this tab's identity
      // can no longer be trusted. Compare sub to our current user id.
      const payload = decodeTokenPayload(newToken);
      const newSub = payload && payload.sub;
      const currentId = user && typeof user === "object" ? user.id : null;

      if (!newSub) {
        // Garbage token written by something else — invalidate defensively.
        wipeLocalSession();
        setUser(false);
        if (!window.location.pathname.startsWith("/login")) {
          window.location.replace("/login");
        }
        return;
      }
      if (currentId && newSub === currentId) {
        // Same user (maybe refreshed token). Nothing to do — keep this tab
        // consistent, don't force a logout.
        return;
      }
      // Different user signed in on the same origin → invalidate THIS tab.
      wipeLocalSession();
      setUser(false);
      if (!window.location.pathname.startsWith("/login")) {
        window.location.replace("/login");
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [user]);

  // ---------------------------------------------------------------------
  // MOUNT: reconcile the displayed identity against the LIVE token.
  //
  // Previously we set `user` from login's response payload and kept it as
  // the source of truth, which meant the UI identity could drift from
  // `localStorage.token` (if token was overwritten by another session, by
  // a stale token restore path, by DevTools, etc.). From V9 onward the UI
  // identity is ALWAYS derived from `/auth/me` + the token actually in
  // localStorage. On any divergence we force a hard logout and redirect
  // to /login so the user can never operate as one user while sending
  // Bearer tokens for another.
  // ---------------------------------------------------------------------
  useEffect(() => {
    (async () => {
      const token = (() => {
        try { return localStorage.getItem("token"); } catch { return null; }
      })();
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }

      // Fetch /auth/me with the live token — the global axios interceptor
      // attaches Authorization from that same localStorage key, so this
      // is the canonical "who does the server think I am?" query.
      let me = null;
      try {
        const { data } = await api.get("/auth/me");
        me = data;
      } catch {
        // Token invalid / expired / backend unreachable → wipe & redirect.
        wipeLocalSession();
        setUser(false);
        setLoading(false);
        // Only bounce to /login if we weren't already there (avoids a
        // noisy flash on the login screen itself).
        if (typeof window !== "undefined"
            && !window.location.pathname.startsWith("/login")) {
          window.location.replace("/login");
        }
        return;
      }

      // Sanity check: the Bearer token the client is holding must point to
      // the SAME user id the server resolved. If they diverge, something
      // has gone wrong (stale token, cross-session leftover, manual
      // tampering) and the only safe path is to wipe + re-authenticate.
      const payload = decodeTokenPayload(token);
      const tokenSub = payload && payload.sub;
      if (!tokenSub || !me?.id || tokenSub !== me.id) {
        wipeLocalSession();
        setUser(false);
        setLoading(false);
        if (typeof window !== "undefined"
            && !window.location.pathname.startsWith("/login")) {
          window.location.replace("/login");
        }
        return;
      }

      setUser(me);
      setLoading(false);
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) {
        try { localStorage.setItem("token", data.token); }
        catch { /* storage disabled — login still ok via cookie */ }
      }
      // After login, DO NOT trust `data.user` as the long-lived identity.
      // Re-resolve via /auth/me so the displayed user is always derived
      // from the token actually in localStorage (closes the UI-vs-token
      // divergence window). Also verifies the token sub matches the
      // freshly-returned user id.
      try {
        const { data: me } = await api.get("/auth/me");
        const payload = decodeTokenPayload(data.token);
        if (payload?.sub && me?.id && payload.sub !== me.id) {
          // Mismatch at the moment of login — refuse to establish the
          // session rather than let the user operate inconsistently.
          wipeLocalSession();
          setUser(false);
          return { ok: false, error: "Session integrity check failed. Please try again." };
        }
        setUser(me);
      } catch {
        // /auth/me failed right after login (shouldn't normally happen).
        // Fall back to the login response but keep the token so a retry
        // via mount reconciliation can recover.
        setUser(data.user || false);
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    // Fire-and-forget server logout. We MUST clean the local session even
    // if the server call fails (offline, 5xx, aborted) — otherwise the
    // stale localStorage/cookie survives and the next sign-in page would
    // auto-restore the previous user via /auth/me on mount.
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    wipeLocalSession();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
